/**
 * The Help Center's content, assembled: sections, their articles (tokens
 * resolved, bodies parsed), authors, lookups and the most-read list.
 *
 * Pure and Node-importable (relative imports only): the pages read it, and
 * so does the build-time pre-renderer that writes static HTML and the sitemap
 * for search engines (scripts/prerender-help.mjs).
 */
import { HELP_SECTIONS } from './sections.js';
import { getAuthor } from './authors.js';
import { HELP_FLAGS, flagEnabled } from './flags.js';
import { parseMarkdown, toPlainText } from './markdown.js';
import { resolveTokens } from './tokens.js';
import { POPULAR_ARTICLE_IDS } from './popular.js';

import gettingStarted from './articles/getting-started.js';
import booking from './articles/booking.js';
import payments from './articles/payments.js';
import missedPayments from './articles/missed-payments.js';
import changesCancellations from './articles/changes-cancellations.js';
import confirmationAndTravel from './articles/confirmation-and-travel.js';
import seatsAndBags from './articles/seats-and-bags.js';
import flightAndHotel from './articles/flight-and-hotel.js';
import travelInsurance from './articles/travel-insurance.js';
import rewards from './articles/rewards.js';
import account from './articles/account.js';
import emails from './articles/emails.js';
import contact from './articles/contact.js';

export const RAW_ARTICLES_BY_SECTION = Object.freeze({
  'getting-started': gettingStarted,
  booking,
  payments,
  'missed-payments': missedPayments,
  'changes-cancellations': changesCancellations,
  'confirmation-and-travel': confirmationAndTravel,
  'seats-and-bags': seatsAndBags,
  'flight-and-hotel': flightAndHotel,
  'travel-insurance': travelInsurance,
  rewards,
  account,
  emails,
  contact,
});

export const HELP_BASE_PATH = '/help';
export const HELP_SITE_ORIGIN = 'https://defera.app';

export const sectionPath = (sectionId) => `${HELP_BASE_PATH}/${sectionId}`;
export const articlePath = (sectionId, slug) => `${HELP_BASE_PATH}/${sectionId}/${slug}`;

/**
 * Build the Help Center for a set of feature flags. Sections and articles
 * whose flag is off are left out entirely, links to them included.
 */
export function buildHelpCenter(flags = HELP_FLAGS) {
  const sections = [];
  const articles = [];
  for (const def of HELP_SECTIONS) {
    if (!flagEnabled(def.flag, flags)) continue;
    const section = { ...def, path: sectionPath(def.id), authorProfile: getAuthor(def.author), articles: [] };
    for (const raw of RAW_ARTICLES_BY_SECTION[def.id] || []) {
      if (!flagEnabled(raw.flag, flags)) continue;
      const body = resolveTokens(raw.body);
      const { blocks, headings } = parseMarkdown(body);
      const authorId = raw.author || def.author;
      const article = {
        ...raw,
        title: resolveTokens(raw.title),
        summary: resolveTokens(raw.summary),
        id: `${def.id}/${raw.slug}`,
        sectionId: def.id,
        sectionTitle: def.title,
        path: articlePath(def.id, raw.slug),
        author: authorId,
        authorProfile: getAuthor(authorId),
        body,
        blocks,
        headings,
        plainText: toPlainText(blocks),
      };
      section.articles.push(article);
      articles.push(article);
    }
    // A section whose articles are all hidden isn't shown at all.
    if (section.articles.length) sections.push(section);
  }

  const byId = new Map(articles.map((a) => [a.id, a]));
  const sectionById = new Map(sections.map((s) => [s.id, s]));
  // A related link whose article is hidden by a flag simply drops out.
  for (const a of articles) {
    a.relatedArticles = (a.related || []).map((id) => byId.get(id)).filter(Boolean);
  }
  const popular = POPULAR_ARTICLE_IDS.map((id) => byId.get(id)).filter(Boolean);

  return {
    sections,
    articles,
    popular,
    getSection: (id) => sectionById.get(id) || null,
    getArticle: (sectionId, slug) => byId.get(`${sectionId}/${slug}`) || null,
  };
}

export const HELP_CENTER = buildHelpCenter();
